import { Stack, Typography, IconButton, TextField } from '@mui/material';
import { useTBContext } from "../../Contexts/useTBStates";
import { ChangeEvent, useEffect, useState } from 'react';
import { StyledIconButton } from '../../../shared/Components/StyledIconButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface NassauPointItem {
    name: string;
    value: number;
}

const nassauPointsTemplate: NassauPointItem[] = [
    {
        name: '1-18',
        value: 1
    },
]

const isNassauPointItem = (value: unknown): value is NassauPointItem => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    const name = (value as { name?: unknown }).name;
    const pointsValue = (value as { value?: unknown }).value;
    return typeof name === "string" && typeof pointsValue === "number";
};

const getCurrentHolePoints = (viewEvent: any, roundValue: number): NassauPointItem[] => {
    const maybeHolePoints = viewEvent?.rounds?.[roundValue - 1]?.holePoints;
    if (!Array.isArray(maybeHolePoints) || maybeHolePoints.length === 0) return nassauPointsTemplate;
    return maybeHolePoints.every(isNassauPointItem) ? maybeHolePoints : nassauPointsTemplate;
};

interface PointsTextFieldProps {
  factor: number;
  appTheme: any;
  value: number;
  disabled?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const PointsTextField = ({ factor, appTheme, ...rest }: PointsTextFieldProps) => (
  <TextField
    {...rest}
    size="small"
    sx={{
      width: 64 * (factor || 1),
      padding: 0,
      "& .MuiInputBase-input": {
        fontSize: 14 * (factor || 1),
        height: 24 * (factor || 1),
        padding: '4px 6px',
        textAlign: 'center',
      },
      '& .MuiOutlinedInput-root': { padding: 0 },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: appTheme.palette.primary.main,
      },
    }}
  />
);

export const NassauPointsLayout = () => {

    const { viewEvent, roundValue, editMode, appTheme, factor, setViewEvent } = useTBContext();

    const [holePoints, setHolePoints] = useState<NassauPointItem[]>(getCurrentHolePoints(viewEvent, roundValue));

    useEffect(() => {
        setHolePoints(getCurrentHolePoints(viewEvent, roundValue));
    }, [viewEvent, roundValue]);

    const updateViewEvent = (nextHolePoints: NassauPointItem[]) => {
        if (!viewEvent?.rounds?.[roundValue - 1]) return;

        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        newViewEvent.rounds[roundValue-1].holePoints = nextHolePoints;
        setViewEvent(JSON.parse(JSON.stringify(newViewEvent)));
    }

    const handlePointsFormatChange = (roundIndex: number, holeSpacing: number) => {
        if (!viewEvent?.rounds?.[roundIndex]) return;

        const updatedRounds = JSON.parse(JSON.stringify(viewEvent.rounds));
        const nextHolePoints: NassauPointItem[] = [];
        const rowQty = 18 / holeSpacing;

        for (let i = 0; i < rowQty; i++) {
            nextHolePoints.push({
                name: `${i*holeSpacing + 1}-${(i+1)*holeSpacing}`,
                value: 1
            });
        }

        if (rowQty > 1) {
            nextHolePoints.push({
                name: '1-18',
                value: 1
            });
        }

        updatedRounds[roundIndex].holePoints = nextHolePoints;
        setViewEvent({...viewEvent, rounds: updatedRounds});
    }

    const handlePointsChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        const updatedHolePoints = JSON.parse(JSON.stringify(holePoints)) as NassauPointItem[];
        updatedHolePoints[index].value = Number(e.target.value);
        setHolePoints(updatedHolePoints);
        updateViewEvent(updatedHolePoints);
    }

    const handleDelete = (index: number) => {
        const updatedHolePoints = JSON.parse(JSON.stringify(holePoints));
        updatedHolePoints.splice(index, 1);
        setHolePoints(updatedHolePoints);
        updateViewEvent(updatedHolePoints);
    }

    const splitButtonSx = (active: boolean) => ({
        width: 28 * factor,
        height: 28 * factor,
        fontSize: 12,
        backgroundColor: active ? 'primary.light' : 'action.hover',
        color: active ? 'secondary.main' : 'primary.main',
    });

    return (
        <Stack direction="column" spacing={1.25} sx={{ width: '100%', minWidth: 0, pt: 0.5 }}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                    width: '100%',
                    minWidth: 0,
                    pb: 1,
                    borderBottom: `1px dashed ${appTheme.palette.divider}`,
                }}
            >
                <Typography
                    flex={2}
                    sx={{ fontWeight: 500, fontSize: 14 * factor, minWidth: 0 }}
                >
                    Hole Splits:
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 3, minWidth: 0 }}>
                    <IconButton
                        onClick={() => handlePointsFormatChange(roundValue - 1, 3)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(holePoints.length === 7)}
                    >
                        3
                    </IconButton>
                    <IconButton
                        onClick={() => handlePointsFormatChange(roundValue - 1, 6)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(holePoints.length === 4)}
                    >
                        6
                    </IconButton>
                    <IconButton
                        onClick={() => handlePointsFormatChange(roundValue - 1, 9)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(holePoints.length === 3)}
                    >
                        9
                    </IconButton>
                    <IconButton
                        onClick={() => handlePointsFormatChange(roundValue - 1, 18)}
                        color="primary"
                        disabled={!editMode}
                        sx={splitButtonSx(holePoints.length === 1)}
                    >
                        18
                    </IconButton>
                </Stack>
            </Stack>

            {holePoints.map((holePoint: NassauPointItem, index: number) => (
                <Stack
                    key={`${holePoint.name}-${index}`}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ width: '100%', minWidth: 0 }}
                >
                    <Typography
                        flex={2}
                        sx={{
                            fontWeight: 500,
                            fontSize: 14 * factor,
                            minWidth: 0,
                            color: 'text.primary',
                        }}
                    >
                        {holePoint.name}:
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flex: 3, minWidth: 0 }}
                    >
                        <PointsTextField
                            factor={factor}
                            appTheme={appTheme}
                            value={holePoint.value}
                            disabled={!editMode}
                            onChange={(e) => handlePointsChange(index, e)}
                        />
                        {editMode ? (
                            <StyledIconButton
                                appTheme={appTheme}
                                icon={<DeleteIcon sx={{ fontSize: 16 * factor }} />}
                                label=""
                                factor={factor}
                                color="error"
                                disabled={!editMode}
                                width={25 * factor}
                                height={25 * factor}
                                onClick={() => handleDelete(index)}
                            />
                        ) : null}
                    </Stack>
                </Stack>
            ))}
        </Stack>
    )
}
